import { createSelector } from '@reduxjs/toolkit';
import { partition } from 'lodash';
import { type ItemType } from '../../utils/utils';
import type { Gateway3P, Hive, Room, Sensor, Sensor3P, Spot, Zone } from '../useGetState';
import type { RootState } from '@/redux/store';
import { existsFilter } from '@/utils/filtering';
import pointInPolygon from 'point-in-polygon';
import type { AugmentedFloorPlan } from './reducer';
import { TXYPoint } from '@/types';

const selectSelf = (state: RootState) => {
  return state.studio;
};

export const selectFloorplans = createSelector(selectSelf, (state): AugmentedFloorPlan[] =>
  [...state.base.floorplans, ...state.added.floorplans]
    .map((f) => ({
      ...f,
      opacity: 1,
      ...state.modified.floorplans[f.id],
    }))
    .filter((f) => !state.deleted.includes(f.id)),
);

export const selectDeletedSensors = createSelector(selectSelf, (state): Sensor[] => {
  return state.base.sensors.filter((s) => state.deleted.includes(s.id));
});

export const selectRooms = createSelector(selectSelf, (state): Room[] => {
  return [...state.base.rooms, ...state.added.rooms]
    .map((r) => ({
      ...r,
      ...state.modified.rooms[r.id],
    }))
    .filter((r) => !state.deleted.includes(r.id));
});

export const selectSensors = createSelector(selectSelf, selectRooms, (state, rooms): Sensor[] => {
  return [...state.base.sensors, ...state.added.sensors]
    .map((s) => ({
      ...s,
      ...state.modified.sensors[s.id],
    }))
    .map((s) => {
      const containingRoom = rooms.find((r) =>
        pointInPolygon(s.center, r.coordinates as TXYPoint[]),
      );
      return {
        ...s,
        room: containingRoom
          ? {
              name: containingRoom.name,
              id: containingRoom.id,
              coordinates: containingRoom.coordinates,
              rotation: containingRoom.rotation,
            }
          : undefined,
      };
    })
    .filter((s) => !state.deleted.includes(s.id));
});

export const selectEditingIds = createSelector(selectSelf, (state) => new Set(state.editing));

export const selectIsEditing = (id: string) =>
  createSelector(selectEditingIds, (editingIds) => editingIds.has(id));

export const selectZones = createSelector(selectSelf, (state): Zone[] => {
  return [...state.base.zones, ...state.added.zones]
    .map((z) => ({
      ...z,
      ...state.modified.zones[z.id],
    }))
    .filter((z) => !state.deleted.includes(z.id));
});

export const selectHives = createSelector(selectSelf, (state): Hive[] => {
  return [...state.base.hives, ...state.added.hives]
    .map((z) => ({
      ...z,
      ...state.modified.hives[z.id],
    }))
    .filter((z) => !state.deleted.includes(z.id));
});

export const selectSpots = createSelector(selectSelf, (state): Spot[] => {
  return [...state.base.spots, ...state.added.spots]
    .map((s) => ({
      ...s,
      ...state.modified.spots[s.id],
    }))
    .filter((s) => !state.deleted.includes(s.id));
});

export const selectSensors3p = createSelector(selectSelf, (state): Sensor3P[] => {
  return [...state.base.sensors3p, ...state.added.sensors3p]
    .map((s) => ({
      ...s,
      ...state.modified.sensors3p[s.id],
    }))
    .filter((s) => !state.deleted.includes(s.id));
});

export const selectGateways3p = createSelector(selectSelf, (state): Gateway3P[] => {
  return [...state.base.gateways3p, ...state.added.gateways3p]
    .map((g) => ({
      ...g,
      ...state.modified.gateways3p[g.id],
    }))
    .filter((g) => !state.deleted.includes(g.id));
});

/** Set of spot IDs currently linked to at least one sensor3p. */
export const selectOccupiedSpotIds = createSelector(selectSensors3p, (sensors3p) => {
  return new Set(sensors3p.flatMap((s) => (s.spot_id ? [s.spot_id] : [])));
});

export const selectDirty = createSelector(
  selectSelf,
  (state): boolean =>
    Object.values(state.modified).some((s) => Object.keys(s).length > 0) ||
    Object.values(state.added).some((s) => s.length > 0) ||
    state.deleted.length > 0,
);

export const selectTypesPresent = createSelector(
  selectRooms,
  selectZones,
  selectSensors,
  selectHives,
  selectFloorplans,
  selectSpots,
  selectSensors3p,
  selectGateways3p,
  (rooms, zones, sensors, hives, floorplans, spots, sensors3p, gateways3p): Set<ItemType> => {
    const types = new Set<ItemType>();
    if (rooms.length > 0) {
      types.add('room');
    }
    if (hives.length > 0) {
      types.add('hive');
    }
    if (zones.length > 0) {
      types.add('zone');
    }
    if (sensors.length > 0) {
      types.add('sensor');
    }
    if (floorplans.length > 0) {
      types.add('floorplan');
    }
    if (spots.length > 0) {
      types.add('spot');
    }
    if (sensors3p.length > 0) {
      types.add('sensor3p');
    }
    if (gateways3p.length > 0) {
      types.add('gateway3p');
    }
    return types;
  },
);

export const selectHeatmapStyle = createSelector(selectSelf, (state) => state.heatmapStyle);
export const selectAllLayerStates = createSelector(selectSelf, (state) => state.layers);
export const selectLayerHeatmaps = createSelector(
  selectAllLayerStates,
  (layers) => layers.heatmaps,
);
export const selectLayerFloorplans = createSelector(
  selectAllLayerStates,
  (layers) => layers.floorplans,
);
export const selectLayerRooms = createSelector(selectAllLayerStates, (layers) => layers.rooms);
export const selectLayerZones = createSelector(selectAllLayerStates, (layers) => layers.zones);
export const selectLayerSpots = createSelector(selectAllLayerStates, (layers) => layers.spots);
export const selectLayerSensors3p = createSelector(
  selectAllLayerStates,
  (layers) => layers.sensors3p,
);
export const selectLayerGateways3p = createSelector(
  selectAllLayerStates,
  (layers) => layers.gateways3p,
);
export const selectLayerSensorModel = createSelector(
  selectAllLayerStates,
  (layers) => layers.sensorModel,
);
export const selectLayerHiveModel = createSelector(
  selectAllLayerStates,
  (layers) => layers.hiveModel,
);
export const selectBatteryStatusMode = createSelector(
  selectAllLayerStates,
  (layers) => layers.batteryStatus.visible,
);

export const selectVisibleItems = createSelector(
  selectAllLayerStates,
  selectRooms,
  selectZones,
  selectHives,
  selectSensors,
  selectFloorplans,
  selectSpots,
  selectSensors3p,
  selectGateways3p,
  (
    layers,
    rooms,
    zones,
    hives,
    sensors,
    floorplans,
    spots,
    sensors3p,
    gateways3p,
  ): (Sensor | Room | Zone | Hive | AugmentedFloorPlan | Spot | Sensor3P | Gateway3P)[] => {
    const visibleSensors = layers.sensors.visible ? sensors : [];
    const filteredSensors =
      layers.sensorModel.visible && layers.sensorModel.subLayers?.length
        ? visibleSensors.filter((sensor) =>
            (layers.sensorModel.subLayers ?? []).includes(sensor.model),
          )
        : visibleSensors;

    const visibleHives = layers.hives.visible ? hives : [];
    const filteredHives =
      layers.hiveModel.visible && layers.hiveModel.subLayers?.length
        ? visibleHives.filter(
            (hive) =>
              hive.model_name && (layers.hiveModel.subLayers ?? []).includes(hive.model_name),
          )
        : visibleHives;

    return [
      ...(layers.floorplans.visible ? floorplans : []),
      ...(layers.rooms.visible ? rooms : []),
      ...(layers.zones.visible ? zones : []),
      ...filteredHives,
      ...filteredSensors,
      ...(layers.spots.visible ? spots : []),
      ...(layers.gateways3p.visible ? gateways3p : []),
      ...(layers.sensors3p.visible ? sensors3p : []),
    ];
  },
);

/**
 * Selector for items to use in bounds/center calculation.
 * Always includes floor plans regardless of visibility, since they define
 * the physical space. Other items are included based on visibility.
 */
export const selectItemsForBounds = createSelector(
  selectAllLayerStates,
  selectRooms,
  selectZones,
  selectHives,
  selectSensors,
  selectFloorplans,
  selectSpots,
  selectSensors3p,
  selectGateways3p,
  (
    layers,
    rooms,
    zones,
    hives,
    sensors,
    floorplans,
    spots,
    sensors3p,
    gateways3p,
  ): (Sensor | Room | Zone | Hive | AugmentedFloorPlan | Spot | Sensor3P | Gateway3P)[] => {
    // Always include floor plans for bounds - they define the physical space
    const visibleSensors = layers.sensors.visible ? sensors : [];
    const visibleHives = layers.hives.visible ? hives : [];

    return [
      ...floorplans, // Always include floor plans for bounds calculation
      ...(layers.rooms.visible ? rooms : []),
      ...(layers.zones.visible ? zones : []),
      ...visibleHives,
      ...visibleSensors,
      ...(layers.spots.visible ? spots : []),
      ...(layers.gateways3p.visible ? gateways3p : []),
      ...(layers.sensors3p.visible ? sensors3p : []),
    ];
  },
);

export const selectSelectedIds = createSelector(selectSelf, (state) => {
  // cast to only read methods/properties, we don't want modifications (which wouldn't do anything)
  return new Set(state.selected) as Omit<Set<string>, 'add' | 'clear'>;
});

export const selectErrors = createSelector(selectSelf, (state) => {
  return state.errors;
});

export const selectItemsGroupedBySelection = createSelector(
  selectVisibleItems,
  selectSelectedIds,
  (visibleItems, selectedIds) => {
    const [selected, unselected] = partition(visibleItems, (i) => selectedIds.has(i.id));
    return { selected, unselected };
  },
);

export const selectIsInitialized = createSelector(selectSelf, (state) => state.initialized);

export const selectUnplottedHivesCount = createSelector(
  selectSelf,
  (state) => state.base.unplotted.length - state.added.hives.length,
);
export const selectUnplottedHives = createSelector(selectSelf, (state) => state.base.unplotted);

export const selectCanUndo = createSelector(selectSelf, (state) => state.undoState.length > 0);
export const selectCanRedo = createSelector(selectSelf, (state) => state.redoState.length > 0);

const selectSearch = createSelector(selectSelf, (state) => state.search);
export const selectSearchString = createSelector(selectSearch, (search) => search.text);
export const selectSearchHoverId = createSelector(selectSearch, (search) => {
  return search.hoverItemId;
});

export const selectAreRelatedDevicesHighlighted = createSelector(
  selectSelf,
  (state) => state.highlightRelatedDevices,
);

export const selectHasClipboard = createSelector(selectSelf, (state) => state.clipboard !== null);

export const selectSelectedHovered = createSelector(selectSelf, (state) => state.selectedHovered);

export const selectIsHighlighted = createSelector(
  [selectSelf, (_, id: string) => id],
  (state, id) => {
    if (!state.highlightRelatedDevices) return false;
    // for all selected hives, we highlight sensors paired to them.
    const selectedHiveIds = state.selected.filter((id) => id.startsWith('hive_'));

    // for all selected sensors, we highlight the parent hive and all 'siblings'
    const selectedSensorIds = state.selected.filter((id) => id.startsWith('sensor_'));
    const selectedSensors = state.base.sensors.filter((s) => selectedSensorIds.includes(s.id));

    const hivesToHighlight = new Set<string>([
      ...selectedHiveIds,
      ...selectedSensors.map((s) => s.hive?.id).filter(existsFilter),
    ]);

    // go ahead and return true if the id in question is one of the hives
    if (hivesToHighlight.has(id)) return true;

    // otherwise if the id matches a sensor, does its hive exist in the set?
    const sensor = state.base.sensors.find((s) => s.id === id);
    if (sensor && sensor.hive && hivesToHighlight.has(sensor.hive.id)) return true;

    // nothing matched.
    return false;
  },
);
